/**
 * Reading a table's link to CSRD back from what was stored.
 *
 * Forgiving where a document is, and strict where it matters. A link is used to
 * ask CSRD for something, so one that cannot name it (no entity, no disclosure,
 * no reporting) is not a link at all and the table reads as an ordinary one:
 * refreshing from a half-remembered place is worse than not refreshing. What is
 * only there to be shown (a label, a date, a status) falls back on its own.
 */

import { isJsonObject, readNumber, readString, type JsonValue } from '@/domain/json'
import type { CsrdLink } from '@/domain/section'

function whole(source: JsonValue | undefined, key: string): number {
  const value = readNumber(source, key, 0)
  return Number.isInteger(value) && value > 0 ? value : 0
}

function count(source: JsonValue | undefined, key: string): number {
  const value = readNumber(source, key, 0)
  return Number.isInteger(value) && value >= 0 ? value : 0
}

/** The link stored on a table, or undefined when what is stored cannot name a place in CSRD. */
export function readCsrdLink(stored: JsonValue | undefined): CsrdLink | undefined {
  if (!isJsonObject(stored)) return undefined

  const entityId = whole(stored, 'entityId')
  const roleTypeId = whole(stored, 'roleTypeId')
  const roleCode = readString(stored, 'roleCode').trim()
  const reportingCode = readString(stored, 'reportingCode').trim()
  const tableIndex = count(stored, 'tableIndex')
  if (!entityId || !roleTypeId || !roleCode || !reportingCode) return undefined

  const language = readString(stored, 'language')
  return {
    entityId,
    entityCode: readString(stored, 'entityCode'),
    roleTypeId,
    roleCode,
    ref: readString(stored, 'ref'),
    disclosure: readString(stored, 'disclosure'),
    tableIndex,
    reportingCode,
    reportingLabel: readString(stored, 'reportingLabel'),
    language: language === 'fr' || language === 'en' ? language : '',
    pulledAt: readString(stored, 'pulledAt'),
    collected: count(stored, 'collected'),
    controlled: count(stored, 'controlled'),
    audited: count(stored, 'audited'),
  }
}
